import React, { useState, useEffect } from 'react';
import { AdminHeader } from './AdminLayout';
import { getSettings, updateSettings } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
    Power, 
    Clock,
    Save,
    AlertTriangle,
    Database,
    RefreshCw,
    Trash2,
    Upload,
    Download,
    Server,
    CheckCircle,
    Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function EinstellungenSystem() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [deletingAngebote, setDeletingAngebote] = useState(false);
    const [importing, setImporting] = useState(false);
    
    // Sync states
    const [liveServerUrl, setLiveServerUrl] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [exporting, setExporting] = useState(false);
    
    useEffect(() => {
        fetchSettings();
    }, []);
    
    const fetchSettings = async () => {
        try {
            const response = await getSettings();
            setSettings(response.data);
        } catch (err) {
            console.error('Failed to fetch settings:', err);
            toast.error('Einstellungen konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSave = async () => {
        try {
            setSaving(true);
            await updateSettings(settings);
            toast.success('System-Einstellungen gespeichert');
        } catch (err) {
            console.error('Failed to save settings:', err);
            toast.error('Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };
    
    const handleResetDatabase = async () => {
        if (!window.confirm('Datenbank wirklich komplett zurücksetzen?\n\nALLE Daten werden aus den JSON-Dateien neu geladen. Manuell hinzugefügte Daten gehen verloren!')) {
            return;
        }
        
        try {
            setResetting(true);
            const token = localStorage.getItem('auth_token');
            const response = await axios.post(`${API_URL}/api/admin/reset-database`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Datenbank zurückgesetzt! ${JSON.stringify(response.data.counts)}`);
            
        } catch (err) {
            console.error('Failed to reset database:', err);
            toast.error('Zurücksetzen fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
        } finally {
            setResetting(false);
        }
    };
    
    const handleDeleteAngeboteOnly = async () => {
        if (!window.confirm('Nur Angebote löschen?\n\nStammdaten (Gemeinden, Lehrer, Instrumente, Orte) bleiben erhalten.')) {
            return;
        }
        
        try {
            setDeletingAngebote(true);
            const token = localStorage.getItem('auth_token');
            const response = await axios.post(`${API_URL}/api/admin/reset-angebote`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(response.data.message);
        } catch (err) {
            console.error('Failed to delete Angebote:', err);
            toast.error('Löschen fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
        } finally {
            setDeletingAngebote(false);
        }
    };
    
    const handleCsvImport = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        if (!file.name.endsWith('.csv')) {
            toast.error('Bitte nur CSV-Dateien hochladen');
            return;
        }
        
        try {
            setImporting(true);
            const token = localStorage.getItem('auth_token');
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${API_URL}/api/admin/import-angebote-csv`, formData, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            toast.success(response.data.message);
            
            if (response.data.errors?.length > 0) {
                
                toast.warning(`${response.data.errors.length} Zeilen hatten Probleme. Details in der Konsole.`);
            }
        } catch (err) {
            console.error('Failed to import CSV:', err);
            toast.error('Import fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
        } finally {
            setImporting(false);
            // Reset file input
            event.target.value = '';
        }
    };
    
    const handleDebugFiles = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await axios.get(`${API_URL}/api/admin/debug-files`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            alert('Debug-Info in der Konsole (F12). Dateien vorhanden: ' + 
                Object.entries(response.data.files)
                    .map(([k, v]) => `${k}: ${v.exists ? v.count + ' Einträge' : 'FEHLT'}`)
                    .join(', '));
        } catch (err) {
            console.error('Debug failed:', err);
            toast.error('Debug fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
        }
    };
    
    // Export current data to JSON file for download
    const handleExportData = async () => {
        try {
            setExporting(true);
            const token = localStorage.getItem('auth_token');
            const response = await axios.get(`${API_URL}/api/admin/export-data`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Create download link
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `musikschule_export_${new Date().toISOString().split('T')[0]}.json`;
            a.click();
            window.URL.revokeObjectURL(url);
            
            toast.success(`Export erfolgreich: ${response.data.counts.angebote} Angebote, ${response.data.counts.lehrpersonen} Lehrer`);
        } catch (err) {
            console.error('Export failed:', err);
            toast.error('Export fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
        } finally {
            setExporting(false);
        }
    };
    
    // Sync data to live server
    const handleSyncToLiveServer = async () => {
        if (!liveServerUrl) {
            toast.error('Bitte Live-Server URL eingeben');
            return;
        }
        
        if (!window.confirm(`Daten auf ${liveServerUrl} synchronisieren?\n\nALLE Daten auf dem Live-Server werden durch die aktuellen Daten ERSETZT!`)) {
            return;
        }
        
        try {
            setSyncing(true);
            setSyncResult(null);
            const token = localStorage.getItem('auth_token');
            
            // First, export current data
            const exportResponse = await axios.get(`${API_URL}/api/admin/export-data`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Then, import to live server
            const importResponse = await axios.post(`${liveServerUrl}/api/admin/import-data`, exportResponse.data, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            setSyncResult({
                success: true,
                message: importResponse.data.message,
                imported: importResponse.data.imported
            });
            toast.success('Daten erfolgreich zum Live-Server synchronisiert!');
        } catch (err) {
            console.error('Sync failed:', err);
            setSyncResult({
                success: false,
                message: err.response?.data?.detail || err.message
            });
            toast.error('Sync fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
        } finally {
            setSyncing(false);
        }
    };
    
    // Import data from JSON file
    const handleImportDataFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        if (!file.name.endsWith('.json')) {
            toast.error('Bitte nur JSON-Dateien hochladen');
            return;
        }
        
        if (!window.confirm('Alle aktuellen Daten werden durch die Datei ERSETZT!\n\nFortfahren?')) {
            event.target.value = '';
            return;
        }
        
        try {
            setImporting(true);
            const token = localStorage.getItem('auth_token');
            
            // Read file content
            const text = await file.text();
            const data = JSON.parse(text);
            
            const response = await axios.post(`${API_URL}/api/admin/import-data`, data, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            toast.success(`Import erfolgreich: ${JSON.stringify(response.data.imported)}`);
        } catch (err) {
            console.error('Import failed:', err);
            toast.error('Import fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
        } finally {
            setImporting(false);
            event.target.value = '';
        }
    };
    
    if (loading) {
        return (
            <>
                <AdminHeader title="System" />
                <div className="admin-content">
                    <div className="max-w-4xl space-y-8">
                        {[1,2].map(i => (
                            <div key={i} className="bg-card rounded-2xl p-6 animate-pulse">
                                <div className="h-6 bg-muted rounded w-48 mb-4" />
                                <div className="h-12 bg-muted rounded w-full" />
                            </div>
                        ))}
                    </div>
                </div>
            </>
        );
    }
    
    return (
        <>
            <AdminHeader title="System">
                <Button onClick={handleSave} disabled={saving} className="rounded-xl">
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Speichern...' : 'Speichern'}
                </Button>
            </AdminHeader>
            
            <div className="admin-content">
                <div className="max-w-4xl space-y-8">
                    
                    {/* System Status */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                settings?.maintenance_mode ? 'bg-red-50' : 'bg-green-50'
                            }`}>
                                <Power className={`h-5 w-5 ${
                                    settings?.maintenance_mode ? 'text-red-600' : 'text-green-600'
                                }`} />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">System</h2>
                                <p className="text-sm text-muted-foreground">
                                    Öffentliche Seite aktivieren oder deaktivieren
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 rounded-xl border-2 transition-colors"
                                style={{
                                    backgroundColor: settings?.maintenance_mode ? '#fef2f2' : '#f0fdf4',
                                    borderColor: settings?.maintenance_mode ? '#fecaca' : '#bbf7d0'
                                }}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-3 h-3 rounded-full ${
                                        settings?.maintenance_mode ? 'bg-red-500' : 'bg-green-500 animate-pulse'
                                    }`} />
                                    <div>
                                        <p className="font-bold text-lg">
                                            {settings?.maintenance_mode ? 'Inaktiv' : 'Aktiv'}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            {settings?.maintenance_mode 
                                                ? 'Die öffentliche Seite ist offline'
                                                : 'Die öffentliche Seite ist erreichbar'}
                                        </p>
                                    </div>
                                </div>
                                <Switch 
                                    checked={!settings?.maintenance_mode}
                                    onCheckedChange={(v) => setSettings({...settings, maintenance_mode: !v})}
                                    data-testid="system-status-toggle"
                                    className="data-[state=checked]:bg-green-600"
                                />
                            </div>
                            
                            <div className="form-group">
                                <Label className="form-label">Nachricht im Wartungsmodus</Label>
                                <Input
                                    type="text"
                                    value={settings?.maintenance_message || ''}
                                    onChange={(e) => setSettings({...settings, maintenance_message: e.target.value})}
                                    className="form-input"
                                    placeholder="Die Seite wird gerade gewartet. Bitte versuche es später erneut."
                                />
                                <p className="text-sm text-muted-foreground mt-1">
                                    Wird Besuchern angezeigt wenn das System inaktiv ist. Logo wird automatisch angezeigt.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    {/* CSV Import */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                                <Upload className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Angebote importieren</h2>
                                <p className="text-sm text-muted-foreground">
                                    CSV-Datei mit Unterrichtsangeboten hochladen
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                                <p className="font-medium mb-1">CSV-Format:</p>
                                <p>Spalten: Gemeinde, Unterrichtsort, Instrument, Wochentag, Uhrzeit, Name (Lehrer)</p>
                                <p className="mt-1">Die Stammdaten (Gemeinden, Lehrer, Instrumente, Orte) müssen bereits vorhanden sein.</p>
                            </div>
                            
                            <div className="flex items-center gap-4">
                                <label className="flex-1">
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleCsvImport}
                                        className="hidden"
                                        disabled={importing}
                                    />
                                    <Button 
                                        variant="outline" 
                                        className="w-full rounded-xl cursor-pointer"
                                        disabled={importing}
                                        asChild
                                    >
                                        <span>
                                            <Upload className={`h-4 w-4 mr-2 ${importing ? 'animate-bounce' : ''}`} />
                                            {importing ? 'Importiere...' : 'CSV-Datei auswählen'}
                                        </span>
                                    </Button>
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    {/* Datenbank */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                                <Database className="h-5 w-5 text-orange-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Datenbank</h2>
                                <p className="text-sm text-muted-foreground">
                                    Daten zurücksetzen und verwalten
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            {/* Delete only Angebote */}
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-sm text-yellow-800 mb-3">
                                    <strong>Nur Angebote löschen:</strong> Stammdaten (Gemeinden, Lehrer, Instrumente, Orte) bleiben erhalten.
                                </p>
                                <Button 
                                    variant="outline"
                                    onClick={handleDeleteAngeboteOnly}
                                    disabled={deletingAngebote}
                                    className="rounded-xl border-yellow-400 text-yellow-700 hover:bg-yellow-100"
                                >
                                    <Trash2 className={`h-4 w-4 mr-2 ${deletingAngebote ? 'animate-pulse' : ''}`} />
                                    {deletingAngebote ? 'Lösche Angebote...' : 'Nur Angebote löschen'}
                                </Button>
                            </div>
                            
                            {/* Full Reset - Button ausgeblendet, API bleibt bestehen */}
                            {/* 
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-800 mb-3">
                                    <strong>Achtung:</strong> Komplettes Zurücksetzen löscht ALLE Daten und lädt sie aus den JSON-Dateien neu.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <Button 
                                        variant="destructive" 
                                        onClick={handleResetDatabase}
                                        disabled={resetting}
                                        className="rounded-xl"
                                    >
                                        <RefreshCw className={`h-4 w-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
                                        {resetting ? 'Wird zurückgesetzt...' : 'Komplett zurücksetzen'}
                                    </Button>
                                    
                                    <Button 
                                        variant="outline" 
                                        onClick={handleDebugFiles}
                                        className="rounded-xl"
                                    >
                                        <Database className="h-4 w-4 mr-2" />
                                        Debug: Dateien prüfen
                                    </Button>
                                </div>
                            </div>
                            */}
                        </div>
                    </div>
                    
                    {/* Session Einstellungen */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                <Clock className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Session Einstellungen</h2>
                                <p className="text-sm text-muted-foreground">
                                    Timeout und Sicherheitseinstellungen
                                </p>
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <Label className="form-label">Session-Timeout (Stunden)</Label>
                            <Input
                                type="number"
                                min="1"
                                max="168"
                                value={settings?.session_timeout_hours || 8}
                                onChange={(e) => setSettings({...settings, session_timeout_hours: parseInt(e.target.value)})}
                                className="form-input max-w-xs"
                            />
                            <p className="text-sm text-muted-foreground mt-2">
                                Nach dieser Zeit müssen sich Benutzer erneut anmelden.
                            </p>
                        </div>
                    </div>
                    
                    {/* Daten-Synchronisation */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                                <Server className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Daten-Synchronisation</h2>
                                <p className="text-sm text-muted-foreground">
                                    Aktuellen Datenstand exportieren oder auf Live-Server übertragen
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-6">
                            {/* Export to JSON */}
                            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                                <p className="text-sm text-emerald-800 mb-3">
                                    <strong>Daten exportieren:</strong> Alle Daten als JSON-Datei herunterladen (Backup oder manueller Transfer)
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <Button 
                                        variant="outline"
                                        onClick={handleExportData}
                                        disabled={exporting}
                                        className="rounded-xl border-emerald-400 text-emerald-700 hover:bg-emerald-100"
                                    >
                                        {exporting ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Download className="h-4 w-4 mr-2" />
                                        )}
                                        {exporting ? 'Exportiere...' : 'Als JSON exportieren'}
                                    </Button>
                                    
                                    <label>
                                        <input
                                            type="file"
                                            accept=".json"
                                            onChange={handleImportDataFile}
                                            className="hidden"
                                            disabled={importing}
                                        />
                                        <Button 
                                            variant="outline" 
                                            className="rounded-xl cursor-pointer border-emerald-400 text-emerald-700 hover:bg-emerald-100"
                                            disabled={importing}
                                            asChild
                                        >
                                            <span>
                                                <Upload className={`h-4 w-4 mr-2 ${importing ? 'animate-bounce' : ''}`} />
                                                {importing ? 'Importiere...' : 'JSON importieren'}
                                            </span>
                                        </Button>
                                    </label>
                                </div>
                            </div>
                            
                            {/* Sync to Live Server */}
                            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                                <p className="text-sm text-purple-800 mb-3">
                                    <strong>Auf Live-Server übertragen:</strong> Alle Daten direkt auf den Produktiv-Server synchronisieren
                                </p>
                                <div className="space-y-3">
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="https://musikschule-live.example.com"
                                            value={liveServerUrl}
                                            onChange={(e) => setLiveServerUrl(e.target.value)}
                                            className="flex-1"
                                        />
                                        <Button 
                                            onClick={handleSyncToLiveServer}
                                            disabled={syncing || !liveServerUrl}
                                            className="rounded-xl bg-purple-600 hover:bg-purple-700"
                                        >
                                            {syncing ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <RefreshCw className="h-4 w-4 mr-2" />
                                            )}
                                            {syncing ? 'Synchronisiere...' : 'Synchronisieren'}
                                        </Button>
                                    </div>
                                    
                                    {syncResult && (
                                        <div className={`p-3 rounded-lg flex items-start gap-2 ${
                                            syncResult.success 
                                                ? 'bg-green-100 border border-green-200' 
                                                : 'bg-red-100 border border-red-200'
                                        }`}>
                                            {syncResult.success ? (
                                                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                                            ) : (
                                                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                                            )}
                                            <div>
                                                <p className={`text-sm font-medium ${syncResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                                    {syncResult.message}
                                                </p>
                                                {syncResult.imported && (
                                                    <p className="text-xs text-green-700 mt-1">
                                                        Importiert: {Object.entries(syncResult.imported).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-purple-600 mt-2">
                                    Hinweis: Du musst auf beiden Servern mit demselben Admin-Account eingeloggt sein
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
